import store from '@/store';

/**
 * 返回经过服务器-客户端时间差调整的当前时间（Date 对象）
 */
/**
 * 获取当前时间或解析并返回传入时间的时间戳（毫秒）
 * - 当传入 `time` 时，尝试将其解析为时间戳（ms）并返回 number
 * - 当未传入 `time` 时，返回经过 timeDifference 校准的 Date（若差值在 1s 内则返回本地 Date）
 *
 * 支持的 `time` 格式：
 * - 数字（ms 或秒）或数字字符串
 * - 可被 `new Date(...)` 解析的字符串（例如 '2025-11-12 20:50:34'）
 *
 * @param {Date|string|number} [time] - 可选，目标时间；若提供则返回时间戳（毫秒）
 * @returns {Date|number}
 */
export function getCurrentTime(time) {
  const timeDifference = store.state.account.timeDifference || 0;

  // 如果传入了 time 参数，尝试解析并返回时间戳（毫秒）
  if (time !== undefined && time !== null) {
    const ts = parseToTimestamp(time);
    if (isNaN(ts)) return NaN;
    // 对传入的时间也应用服务器-客户端的校准差（除非差值在 1 秒以内）
    if (Math.abs(timeDifference) <= 1000) {
      return ts;
    }
    return ts - timeDifference;
  }

  // 如果服务器与客户端时间差在 1 秒以内，认为无需校准，直接使用本地时间
  if (Math.abs(timeDifference) <= 1000) {
    return new Date();
  }
  return new Date(Date.now() - timeDifference);
}
export function getFutureTime(time) {
  const timeDifference = store.state.account.timeDifference || 0;

  // 如果传入了 time 参数，尝试解析并返回时间戳（毫秒）
  if (time !== undefined && time !== null) {
    const ts = parseToTimestamp(time);
    if (isNaN(ts)) return NaN;
    // 对传入的时间也应用服务器-客户端的校准差（除非差值在 1 秒以内）
    if (Math.abs(timeDifference) <= 1000) {
      return ts;
    }
    return ts + timeDifference;
  }

  // 如果服务器与客户端时间差在 1 秒以内，认为无需校准，直接使用本地时间
  if (Math.abs(timeDifference) <= 1000) {
    return new Date();
  }
  return new Date(Date.now() + timeDifference);
}


/**
 * 将多种时间表示解析为毫秒时间戳（number）。尽量兼容常见格式：
 * - 数字（秒或毫秒）或数字字符串
 * - 'YYYY-MM-DD HH:mm:ss' 类型的字符串（会尝试替换空格为 'T' 以便解析）
 * - 其它可被 Date 解析的字符串
 * 若无法解析，返回 NaN。
 * @param {Date|string|number} value
 * @returns {number}
 */
function parseToTimestamp(value) {
  if (value instanceof Date) {
    return value.getTime();
  }

  // 数字（或数字字符串）处理：判断可能为秒级还是毫秒级
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value.trim()))) {
    const num = typeof value === 'number' ? value : Number(value.trim());
    // 如果看起来像秒级（小于 1e11），则乘以 1000
    if (Math.abs(num) < 1e11) {
      return num * 1000;
    }
    return num;
  }

  // 字符串解析：优先尝试直接解析，然后尝试把空格替换为 'T'，再尝试把 '-' 换成 '/'（兼容部分浏览器）
  if (typeof value === 'string') {
    const s = value.trim();
    let d = new Date(s);
    if (!isNaN(d.getTime())) return d.getTime();

    // 'YYYY-MM-DD HH:mm:ss' -> 'YYYY-MM-DDTHH:mm:ss'
    d = new Date(s.replace(' ', 'T'));
    if (!isNaN(d.getTime())) return d.getTime();

    // 兼容 'YYYY/MM/DD HH:mm:ss' 的解析
    d = new Date(s.replace(/-/g, '/'));
    if (!isNaN(d.getTime())) return d.getTime();
  }

  return NaN;
}

